import { useState } from "react";

import Icons from "assets";
import { Button } from "ui/button";
import toast from "components/partials/toast/toast";
import CollegeProfileBasicInformationForm, {
  type BasicInformationFormValues,
} from "containers/admin/clients/client/college-profile-page/college-profile-basic-information-form";
import CollegeProfileEditActions from "containers/admin/clients/client/college-profile-page/college-profile-edit-actions";
import { maskSecret } from "containers/admin/clients/client/college-profile-page/college-profile-iterable-api-key-input";
import CollegeProfileSectionHeader from "containers/admin/clients/client/college-profile-page/college-profile-section-header";

import { useCurrentClient, useReplaceClient } from "hooks/api/use-clients";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import { convertUrlToHttps, getErrorMessage } from "utilities";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

const FORM_ID = "college-profile-basic-information-form";

type ReadOnlyField = {
  label: string;
  value?: string;
  isLink?: boolean;
};

const CollegeProfileBasicInformationSection = ({
  isEditing,
  onEditingChange,
  onOpenDeleteClientModal,
}: {
  isEditing: boolean;
  onEditingChange: (isEditing: boolean) => void;
  onOpenDeleteClientModal: () => void;
}) => {
  const client = useCurrentClient();
  const currentUser = useCurrentUser();
  const replaceClientMutation = useReplaceClient();
  const [isFormDirty, setIsFormDirty] = useState(false);

  const canWriteClient = hasPermission(currentUser, Permission.PERM_CLIENT_WRITE);

  const exitEditMode = () => {
    setIsFormDirty(false);
    onEditingChange(false);
  };

  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: isFormDirty,
    onDiscard: exitEditMode,
  });

  const handleEdit = () => onEditingChange(true);

  const handleSubmit = (values: BasicInformationFormValues) => {
    const { apolloDomainUrl, collegeUrl, privacyPolicyUrl } = values;

    replaceClientMutation.mutate(
      {
        id: client.id,
        data: {
          ...values,
          name: client.name,
          apolloDomainUrl: apolloDomainUrl && convertUrlToHttps(apolloDomainUrl),
          collegeUrl: collegeUrl && convertUrlToHttps(collegeUrl),
          privacyPolicyUrl: privacyPolicyUrl && convertUrlToHttps(privacyPolicyUrl),
        },
      },
      {
        onSuccess: () => {
          toast.success({ data: { title: "Basic information saved" } });
          exitEditMode();
        },
        onError: (err) => {
          toast.error({
            data: {
              title: "Could not save basic information",
              message: getErrorMessage(err),
            },
          });
        },
      },
    );
  };

  const readOnlyFields: ReadOnlyField[] = [
    { label: "Client Iterable project ID", value: client.iterableProjectId },
    { label: "Client prefix", value: client.clientCode },
    ...(canWriteClient
      ? [{ label: "Iterable API key", value: client.iterableApiKey && maskSecret(client.iterableApiKey) }]
      : []),
    { label: "Apollo profile code", value: client.tealiumProfileCode },
    { label: "Client apollo URL", value: client.apolloDomainUrl, isLink: true },
    { label: "Client website URL", value: client.collegeUrl, isLink: true },
    { label: "Client privacy policy URL", value: client.privacyPolicyUrl, isLink: true },
  ];

  const renderReadOnlyField = (field: ReadOnlyField) => (
    <div key={field.label} className="flex flex-col gap-0">
      <dt className="text-sm font-normal text-gray-500">{field.label}</dt>
      <dd className="text-base font-medium text-gray-900">
        {field.isLink ? (
          <a className="text-aqua-600 underline" href={field.value} target="_blank" rel="noreferrer">
            {field.value}
          </a>
        ) : (
          field.value
        )}
      </dd>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {unsavedChangesDialog}
      <CollegeProfileSectionHeader title="Basic information">
        {canWriteClient && (
          <CollegeProfileEditActions
            isEditing={isEditing}
            isSaving={replaceClientMutation.isPending}
            onEdit={handleEdit}
            onCancel={handleCancelClick}
            submitFormId={isEditing ? FORM_ID : undefined}
          />
        )}
      </CollegeProfileSectionHeader>

      {isEditing ? (
        <CollegeProfileBasicInformationForm
          formId={FORM_ID}
          client={client}
          onSubmit={handleSubmit}
          onDirtyChange={setIsFormDirty}
        />
      ) : (
        <dl className="flex max-w-[600px] flex-col gap-4">
          {readOnlyFields.filter((field) => field.value).map(renderReadOnlyField)}
        </dl>
      )}

      {canWriteClient && isEditing && (
        <div className="pt-3">
          <Button
            color="error"
            variant="outlined"
            startIcon={<Icons.Trash />}
            onClick={onOpenDeleteClientModal}
            data-testid="college-profile-delete-client-button"
          >
            Delete client
          </Button>
        </div>
      )}
    </div>
  );
};

export default CollegeProfileBasicInformationSection;
